<script>
export default {
  name: 'ToolRenderer',
  props: {
    tool: { type: Object, required: true },
    args: { type: Object, default: () => ({}) },
    styles: { type: Object, required: true },
    messageIndex: { type: Number, required: false },
  },
  data() {
    const initialFormValues = {};
    const formsConfig = (this.tool?.typeData?.forms || {});
    const inputsConfig = formsConfig.inputs || {};

    if (inputsConfig?.name?.enabled) initialFormValues.name = this.args?.name || '';
    if (inputsConfig?.email?.enabled) initialFormValues.email = this.args?.email || '';
    if (inputsConfig?.phone?.enabled) initialFormValues.phone = this.args?.phone || '';

    const customInputs = Array.isArray(inputsConfig?.custom) ? inputsConfig.custom : [];
    customInputs.forEach((customInput) => {
      if (!customInput) return;
      const isEnabled = customInput.enabled === undefined ? true : Boolean(customInput.enabled);
      if (!isEnabled) return;
      const key = customInput.key;
      if (typeof key === 'string' && key.length > 0) {
        initialFormValues[key] = this.args?.[key] || '';
      }
    });

    return {
      inputValue: this.args?.value || '',
      formValues: initialFormValues,
      isSubmitting: false,
      selectedFile: null,
      fileInputKey: 0,
    };
  },
  computed: {
    toolType() {
      return this.tool.type;
    },
    buttonData() {
      return this.tool?.typeData?.button || {};
    },
    inputData() {
      return this.tool?.typeData?.input || {};
    },
    formsData() {
      return this.tool?.typeData?.forms || {};
    },
    imageUploadData() {
      return this.tool?.typeData?.imageUpload || {};
    },
    multipleChoiceData() {
      return this.tool?.typeData?.multipleChoice || {};
    },
    multipleChoiceOptions() {
      const localized = this.args && Array.isArray(this.args.choiceButtons) ? this.args.choiceButtons : [];
      if (localized && localized.length > 0) return localized;
      const opts = this.multipleChoiceData?.options;
      return Array.isArray(opts) ? opts : [];
    },
    resolvedButtonText() {
      if (this.toolType === 'button') return this.buttonData.buttonText || 'Open';
      if (this.toolType === 'forms') return this.formsData.buttonText || 'Submit';
      if (this.toolType === 'imageUpload') return this.imageUploadData.uploaderText || 'Upload image';
      return 'Submit';
    },
    visibleFormInputs() {
      const inputs = [];
      const inputsConfig = this.formsData?.inputs || {};
      if (inputsConfig?.name?.enabled) {
        inputs.push({ key: 'name', placeholder: inputsConfig?.name?.placeholder || 'Name' });
      }
      if (inputsConfig?.email?.enabled) {
        inputs.push({ key: 'email', placeholder: inputsConfig?.email?.placeholder || 'Email' });
      }
      if (inputsConfig?.phone?.enabled) {
        inputs.push({ key: 'phone', placeholder: inputsConfig?.phone?.placeholder || 'Phone' });
      }
      const customInputs = Array.isArray(inputsConfig?.custom) ? inputsConfig.custom : [];
      customInputs.forEach((customInput) => {
        if (!customInput) return;
        const isEnabled = customInput.enabled === undefined ? true : Boolean(customInput.enabled);
        if (!isEnabled) return;
        const key = customInput.key;
        if (typeof key === 'string' && key.length > 0) {
          inputs.push({ key, placeholder: customInput.placeholder || customInput.label || key });
        }
      });
      return inputs;
    },
    isFormsSubmitDisabled() {
      if (this.isSubmitting) return true;
      return this.visibleFormInputs.some((inputField) => {
        const value = this.formValues?.[inputField.key];
        return value == null || String(value).trim().length === 0;
      });
    },
    acceptedMimeTypes() {
      const mimes = this.imageUploadData?.acceptedMimeTypes || ['image/jpeg', 'image/png', 'image/webp'];
      return mimes.join(',');
    },
    maxFileSizeMB() {
      return this.imageUploadData?.maxFileSizeMB || 3;
    },
    allowMultiple() {
      return this.imageUploadData?.multiple || false;
    }
  },
  methods: {
    onButtonClick() {
      const executionEvent = { kind: 'button', tool: this.tool, args: this.args };
      this.$emit('execute', executionEvent);
    },
    onInputSubmit() {
      if (!this.inputValue || this.isSubmitting) return;
      this.isSubmitting = true;

      // Base payload keys for backend paramMapping
      const data = { inputData: this.inputValue, value: this.inputValue };

      // If redirectUrl has placeholders, assign the inputValue to any missing placeholder keys
      const redirectUrl = this.tool?.typeData?.input?.redirectUrl || '';
      const placeholders = new Set();
      const reBraces = /\{\{\s*([a-zA-Z0-9_\.]+)\s*\}\}/g;
      const reSingle = /\{\s*([a-zA-Z0-9_\.]+)\s*\}/g;
      const reQuery = /\?\{\s*([a-zA-Z0-9_\.]+)\s*\}/g;
      let regexMatch;
      while ((regexMatch = reBraces.exec(redirectUrl)) !== null) placeholders.add(regexMatch[1]);
      while ((regexMatch = reSingle.exec(redirectUrl)) !== null) placeholders.add(regexMatch[1]);
      while ((regexMatch = reQuery.exec(redirectUrl)) !== null) placeholders.add(regexMatch[1]);
      placeholders.forEach((key) => {
        if (data[key] === undefined && this.args?.[key] === undefined) {
          data[key] = this.inputValue;
        }
      });

      const executionEvent = { kind: 'input', tool: this.tool, data, args: this.args };
      this.$emit('execute', executionEvent, () => { this.isSubmitting = false; });
    },
    onFormsSubmit() {
      if (this.isSubmitting || this.isFormsSubmitDisabled) return;
      this.isSubmitting = true;
      const submissionData = {};
      this.visibleFormInputs.forEach((inputField) => {
        submissionData[inputField.key] = this.formValues?.[inputField.key] || '';
      });
      const executionEvent = { kind: 'forms', tool: this.tool, data: submissionData, args: this.args };
      this.$emit('execute', executionEvent, () => { this.isSubmitting = false; });
    },
    onFileChange(event) {
      const files = event.target.files;
      if (!files || files.length === 0) {
        this.selectedFile = null;
        return;
      }

      const file = files[0];
      const maxSize = this.maxFileSizeMB * 1024 * 1024; // Convert MB to bytes

      // Validate file size
      if (file.size > maxSize) {
        alert(`File size must be less than ${this.maxFileSizeMB}MB`);
        this.selectedFile = null;
        this.fileInputKey++; // Reset input
        return;
      }

      // Validate MIME type
      const acceptedTypes = this.imageUploadData?.acceptedMimeTypes || ['image/jpeg', 'image/png', 'image/webp'];
      if (!acceptedTypes.includes(file.type)) {
        alert(`Only ${acceptedTypes.join(', ')} files are allowed`);
        this.selectedFile = null;
        this.fileInputKey++; // Reset input
        return;
      }

      // Immediately upload (no extra button). Show loading state until parent signals completion.
      this.isSubmitting = true;
      const executionEvent = { kind: 'imageUpload', tool: this.tool, file, args: this.args };
      this.$emit('execute', executionEvent, () => {
        this.isSubmitting = false;
        this.selectedFile = null;
        this.fileInputKey++; // Reset input
      });
    },
    onImageUploadSubmit() {},
    onMultipleChoiceSelect(option) {
      if (!option || typeof option !== 'string') return;
      const executionEvent = { kind: 'multipleChoice', tool: this.tool, data: { choiceText: option }, args: this.args, messageIndex: this.messageIndex };
      this.$emit('execute', executionEvent);
    },
    removeFile() {
      this.selectedFile = null;
      this.fileInputKey++; // Reset input
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

<template>
  <div class="tool-renderer">
    <div v-if="toolType === 'button'" class="tool-card">
      <button
        class="tool-btn"
        :style="{ color: styles.footer.button.textColor, backgroundColor: styles.footer.button.backgroundColor }"
        type="button"
        @click="onButtonClick"
      >
        {{ resolvedButtonText }}
      </button>
      <p v-if="buttonData?.confirmationText" class="hint">{{ buttonData.confirmationText }}</p>
    </div>

    <div v-else-if="toolType === 'input'" class="tool-card">
      <input
        class="tool-input"
        v-model="inputValue"
        :placeholder="inputData?.inputPlaceholder || 'Enter value'"
        :style="{ background: styles.footer.input.backgroundColor, color: styles.footer.input.textColor, borderColor: styles.footer.input.borderColor }"
        type="text"
      />
      <button
        class="tool-btn"
        :disabled="isSubmitting || !inputValue"
        :style="{ color: styles.footer.button.textColor, backgroundColor: styles.footer.button.backgroundColor }"
        type="button"
        @click="onInputSubmit"
      >
        {{ resolvedButtonText }}
      </button>
    </div>

    <div v-else-if="toolType === 'forms'" class="tool-card">
      <input
        v-for="inp in visibleFormInputs"
        :key="inp.key"
        class="tool-input"
        v-model="formValues[inp.key]"
        :placeholder="inp.placeholder"
        :style="{ background: styles.footer.input.backgroundColor, color: styles.footer.input.textColor, borderColor: styles.footer.input.borderColor }"
        type="text"
      />
      <button
        class="tool-btn"
        :disabled="isFormsSubmitDisabled"
        :style="{ color: styles.footer.button.textColor, backgroundColor: styles.footer.button.backgroundColor }"
        type="button"
        @click="onFormsSubmit"
      >
        {{ resolvedButtonText }}
      </button>
    </div>

    <div v-else-if="toolType === 'multipleChoice'" class="tool-card">
      <div class="mc-buttons">
        <button
          v-for="opt in multipleChoiceOptions"
          :key="opt"
          class="mc-btn"
          type="button"
          @click="onMultipleChoiceSelect(opt)"
          :style="{ color: styles.footer.button.textColor, backgroundColor: styles.footer.button.backgroundColor }"
        >
          {{ opt }}
        </button>
      </div>
    </div>

    <div v-else-if="toolType === 'imageUpload'" class="tool-card">
      <div class="file-upload-container">
        <input
          :key="fileInputKey"
          ref="fileInput"
          class="file-input-hidden"
          type="file"
          :accept="acceptedMimeTypes"
          :multiple="allowMultiple"
          @change="onFileChange"
        />
        <div
          class="upload-area"
          :style="{ 
            borderColor: styles.footer.input.borderColor,
            backgroundColor: styles.footer.input.backgroundColor 
          }"
          @click="$refs.fileInput.click()"
        >
          <svg v-if="!isSubmitting" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <div v-else class="upload-spinner"></div>
          <p class="upload-hint">{{ isSubmitting ? 'Uploading...' : ('Attach image (Max ' + maxFileSizeMB + 'MB)') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tool-renderer {
  display: flex;
  width: 100%;
}
.tool-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.tool-input {
  height: 2.5rem;
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1.5px solid;
  font-size: 1rem;
  box-sizing: border-box;
}
.tool-btn {
  padding: 10px 12px;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.2s;
}
.tool-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.hint {
  font-size: 12px;
  opacity: 0.8;
  margin: 0;
}
.file-upload-container {
  width: 100%;
}
.file-input-hidden {
  display: none;
}
.upload-area {
  border: 2px dashed;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  min-width: 260px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  gap: 8px;
}
.upload-area:hover {
  border-color: #0070f0;
  background-color: rgba(0, 112, 240, 0.05);
}
.upload-area svg {
  color: #72777A;
  margin-bottom: 8px;
}
.upload-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(0,0,0,0.1);
  border-top-color: #72777A;
  border-radius: 50%;
  animation: tool-upload-spin 0.8s linear infinite;
  margin-bottom: 8px;
}
@keyframes tool-upload-spin {
  to { transform: rotate(360deg); }
}
.upload-text {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: #303437;
}
.upload-hint {
  margin: 0;
  font-size: 0.85rem;
  color: #72777A;
}
.file-preview {
  border: 1.5px solid;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.file-icon {
  font-size: 24px;
  flex-shrink: 0;
}
.file-info {
  flex: 1;
  min-width: 0;
}
.file-name {
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #303437;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size {
  margin: 0;
  font-size: 0.8rem;
  color: #72777A;
}
.file-remove {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #72777A;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}
.file-remove:hover {
  background-color: rgba(255, 0, 0, 0.1);
  color: #ff0000;
}

.mc-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.mc-btn {
  border: none;
  border-radius: 16px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.2s;
}
.mc-btn:hover {
  opacity: 0.9;
}
</style> 